/**
 * Helper functions for finding and selecting workup files from S3 file arrays
 */
import { usePersistedDataStore } from '../../../store.js';
import { getWorkupS3FileName, NEGOTIATION_REPORT_TYPES, WORKUP_REPORT_TYPES } from '../../../../common/mappings.js';
import { nyscefLastModifiedTime } from '../../NyscefUpload/nyscefExhibitUtils.js';

const WORKUP_PDF_BASES = Object.values(NEGOTIATION_REPORT_TYPES).map((t) =>
  String(t.baseSuffix || '').toLowerCase()
);

/**
 * Extracts the filename from a file object (handles Filename, filename, and Key)
 * Backend may send Filename or Key; some serializers use lowercase filename.
 * @param {Object} file - File object with Filename, filename, and/or Key property
 * @returns {string} Lowercase filename
 */
const getFilename = (file) => {
  if (!file) return '';
  const raw = file.Filename ?? file.filename ?? (file.Key ? file.Key.split('/').pop() : '') ?? '';
  return String(raw).toLowerCase();
};

/** True if the file is any generated workup PDF (one-pager, FNMA, nomaps, equity, village, LOA). */
export const isWorkupPdf = (file) => {
  const filename = getFilename(file);
  if (!filename.endsWith('.pdf')) return false;
  return WORKUP_PDF_BASES.some((base) => base && filename.includes(base));
};

/**
 * Finds a workup file from an array of files based on workup type
 * 
 * Business Rules:
 * - For equity/assessment types: Prefer default (non-FNMA) version, fall back to FNMA if no default exists
 * - For sales types: Prioritize FNMA version, fall back to any sales_comps file (one-pager, nomaps, etc.)
 * - For village runs: Look for files with "village_" prefix (e.g., "village_comps_fnma.pdf")
 * 
 * @param {Array} files - Array of S3 file objects with Url, Filename, and/or Key properties
 * @param {string} workupType - Type of workup: 'sales', 'equity', 'assessment', or DB values 'Unequal'/'Excessive'
 * @returns {Object|null} The found file object or null if no match
 */
export const findWorkupFile = (files, workupTypeParam) => {
  if (!files || files.length === 0) {
    return null;
  }

  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const isVillageMode = negotiationObj?.villageRun === 1;
  let workupType = workupTypeParam ?? (negotiationObj?.reportType === 'standard' ? 'sales' : 'assessment');

  // if you are looking for village files, override workup type searchterm
  if (isVillageMode) {
    workupType = 'village';
  }

  let searchTerm = (workupType || '').toLowerCase();
  if (searchTerm === 'unequal' || searchTerm === 'standard') searchTerm = 'sales';
  else if (searchTerm === 'excessive' || searchTerm === 'assessment') searchTerm = 'assessment';

  // Helper function to check if filename matches the workup type and village mode
  const matchesWorkupType = (filename, requireFnma = false) => {
    // Check for village prefix if in village mode
    if (isVillageMode && !filename.includes('village_')) {
      return false;
    }
    
    // Check for FNMA requirement
    if (requireFnma && !filename.includes('fnma')) {
      return false;
    }
    
    // Sales: sales_comps.pdf, sales_comps_fnma.pdf, sales_comps_nomaps.pdf
    if (searchTerm === 'sales') {
      return filename.includes('sales_comps') && !filename.includes('equity');
    }
    if (searchTerm === 'equity' || searchTerm === 'assessment') {
      return filename.includes('equity');
    }
    return filename.includes(searchTerm);
  };

  // For equity/assessment types, prefer default (non-FNMA) version
  if (searchTerm === 'equity' || searchTerm === 'assessment') {
    // First, try to find default (non-FNMA) version
    const defaultFile = files.find(file => {
      const filename = getFilename(file);
      return !filename.includes('fnma') && matchesWorkupType(filename);
    });
    if (defaultFile) {
      return defaultFile;
    }

    // Fall back to any equity/assessment workup file (including FNMA if no default exists)
    return files.find(file => {
      const filename = getFilename(file);
      return matchesWorkupType(filename);
    }) || null;
  }

  // For sales and other types, prioritize FNMA version
  const fnmaFile = files.find(file => {
    const filename = getFilename(file);
    return matchesWorkupType(filename, true);
  });

  if (fnmaFile) {
    return fnmaFile;
  }

  return files.find(file => {
    const filename = getFilename(file);
    return matchesWorkupType(filename);
  }) || null;
};

/**
 * Finds both sales and equity workup files from an array of files
 * @param {Array} files - Array of S3 file objects
 * @returns {Object} Object with salesWorkupFile and equityWorkupFile properties
 */
export const findWorkupFiles = (files) => {
  return {
    salesWorkupFile: findWorkupFile(files, 'sales'),
    equityWorkupFile: findWorkupFile(files, 'equity'),
  };
};

/**
 * Picks the workup PDF for a parcel from S3Files using the same naming as email/workup prep
 * ({@link getWorkupS3FileName} + .pdf). Falls back to {@link findWorkupFile} sales/assessment heuristics.
 * @param {Array} files
 * @param {string} negotiationReportType - e.g. negotiationObj.reportType
 * @param {string} workupReportTypeId - WORKUP_REPORT_TYPES id (fnmaSummary, onepagermaps, loa)
 */
export const findWorkupFileByWorkupReportId = (files, negotiationReportType, workupReportTypeId) => {
  if (!files?.length || !workupReportTypeId) return null;
  const reportType = negotiationReportType || 'standard';
  const base = String(getWorkupS3FileName(reportType, workupReportTypeId) || '').toLowerCase();
  if (!base) return null;
  const expectPdf = `${base}.pdf`;
  const hit = files.find((f) => getFilename(f) === expectPdf);
  if (hit) return hit;
  return findWorkupFile(files, reportType === 'assessment' ? 'assessment' : 'sales');
};

/** Exact workup PDFs for the chosen type (FNMA, one pager, LOA). Does not fall back to a different type. */
export const findRunWorkupFiles = (files, workupReportTypeId = 'fnmaSummary') => {
  if (!files?.length) return [];
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const reportType = String(negotiationObj?.reportType || 'standard').toLowerCase();
  const village = negotiationObj?.villageRun === 1 || reportType === 'village';
  const workup = WORKUP_REPORT_TYPES.find((r) => r.id === workupReportTypeId) || WORKUP_REPORT_TYPES[0];
  const label = workup.label;

  if (workup.internalFileName) {
    const exact = files.find((file) => file?.Url && getFilename(file) === `${workup.internalFileName}.pdf`);
    return exact ? [{ file: exact, title: label }] : [];
  }

  const pairs = village
    ? [[NEGOTIATION_REPORT_TYPES.village.baseSuffix, label]]
    : reportType === 'assessment'
      ? [[NEGOTIATION_REPORT_TYPES.assessment.baseSuffix, label]]
      : reportType === 'evidence_comparison'
        ? [
            [NEGOTIATION_REPORT_TYPES.standard.baseSuffix, `${NEGOTIATION_REPORT_TYPES.standard.displayLabel} ${label}`],
            [NEGOTIATION_REPORT_TYPES.assessment.baseSuffix, `${NEGOTIATION_REPORT_TYPES.assessment.displayLabel} ${label}`],
          ]
        : [[NEGOTIATION_REPORT_TYPES.standard.baseSuffix, label]];

  const suffix = workup.fileSuffix || '';
  const found = [];
  for (const [base, title] of pairs) {
    const exact = files.find((file) => file?.Url && getFilename(file) === `${base}${suffix}.pdf`);
    if (exact) found.push({ file: exact, title });
  }
  return found;
};

/** Newest SCAR filing from the workup viewer Filings tab. Village runs prefer a village SCAR filing. */
export const findScarFilingFile = (files) => {
  if (!files?.length) return null;
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const village = negotiationObj?.villageRun === 1 || String(negotiationObj?.reportType || '').toLowerCase() === 'village';
  const scarFilings = files.filter((file) => {
    if (!file?.Url) return false;
    const base = getFilename(file).replace(/\.pdf$/i, '').replace(/-[a-f0-9]{8}$/i, '');
    return base.includes('filing') && base.includes('scar') && !base.includes('bar');
  });
  if (!scarFilings.length) return null;
  const preferred = scarFilings.filter((file) => getFilename(file).includes('village') === village);
  const pool = preferred.length ? preferred : scarFilings;
  return pool.reduce((best, file) => {
    if (!best) return file;
    const ta = nyscefLastModifiedTime(file);
    const tb = nyscefLastModifiedTime(best);
    if (ta == null) return best;
    if (tb == null) return file;
    return ta >= tb ? file : best;
  }, null);
};
